import React, { useEffect, useRef, useState } from 'react';

//*MUI
import { Box, Modal, TextField } from '@mui/material';

//*files
import cssClasses from "./ViewSiteVisitDetails.module.css";
import Close from '../../Components/Icons/Close';
import ProjectIcon from '../../Assets/Images/Sustenance/ProjectIcon.svg';
import Button from '../../Subcomponents/Button';

// `enableZoom` (photo zoom) and `showVerification` (remarks/verify) are opt-in so the
// existing Sustenance usage is unchanged. `canApprove` shows the Verify button; `onVerify(data)`
// runs after the user confirms in the confirmation popup.
function ViewSiteVisitDetails({ open, handleClose, data, enableZoom = false, showVerification = false, canApprove = false, onVerify, onReject }) {

  const [zoomOpen, setZoomOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [comment, setComment] = useState('');
  const [commentError, setCommentError] = useState('');
  // Which action the confirm popup will run: 'verify' or 'reject'.
  const [pendingAction, setPendingAction] = useState('verify');

  // Verification state comes from the siteVisitVerificationStatus column
  // ('approved' | 'rejected' | 'pending').
  const isApproved = data?.sitevisitverificationstatus === 'approved';
  const isRejected = data?.sitevisitverificationstatus === 'rejected';
  const isPending = !isApproved && !isRejected;

  // Image zoom/pan state for the fullscreen zoom view.
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const lastPosRef = useRef({ x: 0, y: 0 });

  // Reset zoom/pan whenever the zoom view is closed so it reopens at 1x.
  useEffect(() => {
    if (!zoomOpen) {
      setScale(1);
      setOffset({ x: 0, y: 0 });
      setDragging(false);
    }
  }, [zoomOpen]);

  const MIN_SCALE = 1;
  const MAX_SCALE = 5;
  const clampScale = (s) => Math.min(MAX_SCALE, Math.max(MIN_SCALE, s));

  // Mouse-wheel zoom: scroll up = zoom in, down = zoom out. At 1x, pan is reset.
  const handleWheel = (e) => {
    const next = clampScale(scale + (e.deltaY < 0 ? 0.25 : -0.25));
    setScale(next);
    if (next === MIN_SCALE) setOffset({ x: 0, y: 0 });
  };

  // Drag to pan (only meaningful when zoomed in).
  const handleMouseDown = (e) => {
    if (scale === MIN_SCALE) return;
    setDragging(true);
    lastPosRef.current = { x: e.clientX, y: e.clientY };
  };
  const handleMouseMove = (e) => {
    if (!dragging || scale === MIN_SCALE) return;
    const dx = e.clientX - lastPosRef.current.x;
    const dy = e.clientY - lastPosRef.current.y;
    lastPosRef.current = { x: e.clientX, y: e.clientY };
    setOffset((o) => ({ x: o.x + dx, y: o.y + dy }));
  };
  const handleMouseUp = () => setDragging(false);

  const zoomIn = () => setScale((s) => clampScale(s + 0.5));
  const zoomOut = () =>
    setScale((s) => {
      const next = clampScale(s - 0.5);
      if (next === MIN_SCALE) setOffset({ x: 0, y: 0 });
      return next;
    });
  const resetZoom = () => {
    setScale(1);
    setOffset({ x: 0, y: 0 });
  };

  // Format an epoch-millisecond timestamp into a readable date + time.
  const formatDateTime = (val) => {
    if (!val) return '-';
    const d = new Date(Number(val));
    return `${d.toDateString()}, ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
  };

  // Duration between start and end (epoch ms), broken into h/m/s/ms.
  const formatDuration = (start, end) => {
    if (!start || !end) return '-';
    let ms = Number(end) - Number(start);
    if (isNaN(ms) || ms < 0) return '-';
    const hours = Math.floor(ms / 3600000);
    ms %= 3600000;
    const minutes = Math.floor(ms / 60000);
    ms %= 60000;
    const seconds = Math.floor(ms / 1000);
    const milliseconds = ms % 1000;
    return `${hours}h ${minutes}m ${seconds}s ${milliseconds}ms`;
  };

const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    // Cap to the viewport so the modal is always fully visible (header stays
    // pinned, the body scrolls) — long content can never push the close button
    // or the Verify button off-screen.
    maxHeight: '90vh',
    // On mobile, take up most of the screen (90vw) instead of shrinking to
    // fit the content — otherwise the modal looks uncomfortably thin. On
    // larger screens keep it content-sized, capped at 90vw.
    width: { xs: '90vw', sm: 'auto' },
    maxWidth: '90vw',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    bgcolor: 'background.paper',
    borderRadius: '8px',
    boxShadow: 24,
  }

  useEffect(() => {
    console.log("data" , data)
  }, [])
  
          

  return (
    <>
     <Modal open={open} 
        onClose={(_, reason) => {
          if (reason !== "backdropClick") {
            handleClose();
          }
        }}>
        <Box sx={style}>
        <Box className={cssClasses.headerDiv} sx={{ flexShrink: 0 }}>
            <Box className={cssClasses.flexRow}>
              <Box className={cssClasses.flexCol}>
                <Box className={cssClasses.HeaderTitle} mt={1}>
                  Site Visit Details
                </Box>{' '}
              </Box>
              <Box
                ml={'auto'}
                mt={1}
                onClick={handleClose}
                style={{ cursor: 'pointer' }}
              >
                <Close width={'1.2rem'} height={'1.2rem'} />
              </Box>
            </Box>
          </Box>

          <Box flex={1} minHeight={0} overflow={"auto"}>
            <Box className={cssClasses.flexRow} sx={{ margin: { xs: '2rem', sm: '3.5rem' } }}>
            <Box marginTop={"2rem"} >
              <Box className={cssClasses.FirstTitle}>{data?.leadname || data?.leadId?.personId?.name || "-"}</Box>
              <Box className={cssClasses.semiBoldTitle}>  VJ{data?.leaddisplayid? data?.leaddisplayid?.replace(data?.leaddisplayid?.substring(8,12) , "-" + data?.leaddisplayid?.substring(8,12)) :data?.leadId?.leadDisplayId
                        ? data?.leadId?.leadDisplayId?.replace(
                            data?.leadId?.leadDisplayId?.substring(8, 12),
                            '-' + data?.leadId?.leadDisplayId?.substring(8, 12)
                          )
                        : '-'}</Box>
              <Box className={cssClasses.SecondTitle}>{data?.email || data?.leadId?.personId?.email || "-"} | {data?.number || data?.leadId?.personId?.contactNumber || "-"}</Box>
              <Box className={cssClasses.SecondTitle}>{new Date(data?.sitevisitdate || data?.dateTime).toDateString()},{' '}
            {new Date(data?.sitevisitdate || data?.dateTime).toLocaleTimeString([], {
              hour: '2-digit',
              minute: '2-digit',
            })}</Box>
            </Box>
            <Box>
              { data.sitevisiturl ? <img
              style={{
                width: '18rem',
                height: 'auto',
                maxHeight: '24rem',
                objectFit: 'contain',
                borderRadius: '8px',
                cursor: enableZoom ? 'zoom-in' : 'default',
              }}
              src={
                process.env.REACT_APP_SUPABASE_BUCKET_PREFIX +
                 data.sitevisiturl
              }
              alt={''}
              onClick={() => {
                if (enableZoom && data.sitevisiturl) setZoomOpen(true);
              }}
            /> : <img    style={{
              width: '14rem',
              height: '14rem',
              objectFit: 'cover',

            }}
            src={ProjectIcon} alt={''} />}

            </Box>
            </Box>
            
            <Box sx={{ margin: { xs: '0 2rem 2rem', sm: '3.5rem' } }}>
            <Box className={cssClasses.semiBoldTitle}>
              Channel Partner : <Box component={"span"} className={cssClasses.BoldTitle}>{data?.billingname || data?.cpId?.billingname || "-"}</Box>
            </Box>
            <Box className={cssClasses.semiBoldTitle}>
              FOS : <Box component={"span"} className={cssClasses.BoldTitle}>{data?.fosname || data?.fosId?.name || "-"}</Box>
            </Box>
            <Box className={cssClasses.semiBoldTitle}>
              Project Name : <Box component={"span"} className={cssClasses.BoldTitle}>{data?.projectname || data?.projectId?.projectName ||"-"}</Box>
            </Box>
            <Box className={cssClasses.semiBoldTitle}>
              Sales Manager : <Box component={"span"} className={cssClasses.BoldTitle}>{data?.username|| data?.userId?.name || "-" }</Box>
            </Box>

            <Box className={cssClasses.semiBoldTitle}>
              Inventory-Type : <Box component={"span"} className={cssClasses.BoldTitle}>{data?.inventorytype? data?.inventorytype :"-" }</Box>
            </Box>
            {data?.mode && (
              <Box className={cssClasses.semiBoldTitle}>
                Mode : <Box component={"span"} className={cssClasses.BoldTitle}>{data?.mode}</Box>
              </Box>
            )}
            {(data?.startdatetime || data?.enddatetime) && (
              <>
                <Box className={cssClasses.semiBoldTitle}>
                  Start Date & Time : <Box component={"span"} className={cssClasses.BoldTitle}>{formatDateTime(data?.startdatetime)}</Box>
                </Box>
                <Box className={cssClasses.semiBoldTitle}>
                  End Date & Time : <Box component={"span"} className={cssClasses.BoldTitle}>{formatDateTime(data?.enddatetime)}</Box>
                </Box>
                <Box className={cssClasses.semiBoldTitle}>
                  Duration : <Box component={"span"} className={cssClasses.BoldTitle}>{formatDuration(data?.startdatetime, data?.enddatetime)}</Box>
                </Box>
              </>
            )}
            {showVerification && (
              <>
                <Box className={cssClasses.semiBoldTitle}>
                  Remarks : <Box component={"span"} className={cssClasses.BoldTitle}>{data?.remarks ? data?.remarks : "-"}</Box>
                </Box>
                <Box className={cssClasses.semiBoldTitle}>
                  Verification : <Box component={"span"} className={cssClasses.BoldTitle}>{isApproved ? "Verified" : isRejected ? "Rejected" : "Verification Pending"}</Box>
                </Box>
                {isApproved ? (
                  <>
                    <Box className={cssClasses.semiBoldTitle}>
                      Verified By : <Box component={"span"} className={cssClasses.BoldTitle}>{data?.verifiedbyname ? data?.verifiedbyname : "-"}</Box>
                    </Box>
                    <Box className={cssClasses.semiBoldTitle}>
                      Comment : <Box component={"span"} className={cssClasses.BoldTitle}>{data?.comment ? data?.comment : "-"}</Box>
                    </Box>
                  </>
                ) : isRejected ? (
                  <>
                    <Box className={cssClasses.semiBoldTitle}>
                      Rejected By : <Box component={"span"} className={cssClasses.BoldTitle}>{data?.verifiedbyname ? data?.verifiedbyname : "-"}</Box>
                    </Box>
                    <Box className={cssClasses.semiBoldTitle}>
                      Comment : <Box component={"span"} className={cssClasses.BoldTitle}>{data?.comment ? data?.comment : "-"}</Box>
                    </Box>
                  </>
                ) : (
                  <Box className={cssClasses.semiBoldTitle}>
                    Verification Pending From : <Box component={"span"} className={cssClasses.BoldTitle}>{data?.userparentname ? data?.userparentname : "-"}</Box>
                  </Box>
                )}
              </>
            )}
            </Box>

            {/* Action row shows ONLY for pending visits. Approved and Rejected are
                terminal — no buttons — so a rejected visit can't be verified again. */}
            {showVerification && canApprove && isPending && (
              <Box sx={{ margin: { xs: '0 2rem 2rem', sm: '0 3.5rem 3rem' } }}>
                <Box className={cssClasses.semiBoldTitle} mb={1}>
                  Comment <Box component={'span'} style={{ color: 'red' }}>*</Box>
                </Box>
                <TextField
                  multiline
                  minRows={3}
                  fullWidth
                  placeholder={'Add a comment'}
                  value={comment}
                  onChange={(e) => {
                    setComment(e.target.value);
                    if (commentError) setCommentError('');
                  }}
                  error={commentError !== ''}
                  helperText={commentError}
                  sx={{
                    '& .MuiInputBase-input': { fontSize: '1.4rem' },
                    '& .MuiFormHelperText-root': { fontSize: '1.2rem' },
                  }}
                />
                <Box display={'flex'} justifyContent={'flex-end'} gap={'1rem'} mt={2}>
                  <Button
                    style={{
                      color: '#FFF',
                      backgroundColor: '#D92D20',
                      border: '1px solid #D92D20',
                      fontSize: '1.4rem',
                      lineHeight: '2rem',
                    }}
                    btnName={'Reject'}
                    padding={'0.7rem 3rem'}
                    clicked={() => {
                      if (!comment.trim()) {
                        setCommentError('Comment is required');
                        return;
                      }
                      setPendingAction('reject');
                      setConfirmOpen(true);
                    }}
                  />
                  <Button
                    style={{
                      color: '#FFF',
                      backgroundColor: '#079455',
                      border: '1px solid #079455',
                      fontSize: '1.4rem',
                      lineHeight: '2rem',
                    }}
                    btnName={'Verify Site Visit'}
                    padding={'0.7rem 3rem'}
                    clicked={() => {
                      if (!comment.trim()) {
                        setCommentError('Comment is required');
                        return;
                      }
                      setPendingAction('verify');
                      setConfirmOpen(true);
                    }}
                  />
                </Box>
              </Box>
            )}

          </Box>
        </Box>
        </Modal>

        {confirmOpen && (
          <Modal open={confirmOpen} onClose={() => setConfirmOpen(false)}>
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                bgcolor: 'background.paper',
                borderRadius: '8px',
                boxShadow: 24,
                padding: '3rem',
                width: 'fit-content',
                maxWidth: '90vw',
                outline: 'none',
              }}
            >
              <Box className={cssClasses.HeaderTitle} mb={2}>
                {pendingAction === 'reject' ? 'Confirm Reject' : 'Confirm Verification'}
              </Box>
              <Box className={cssClasses.SecondTitle} mb={4}>
                {pendingAction === 'reject'
                  ? 'Are you sure you want to reject this site visit?'
                  : 'Are you sure you want to approve this site visit?'}
              </Box>
              <Box display={'flex'} justifyContent={'flex-end'} gap={'1rem'}>
                <Button
                  style={{
                    color: '#344054',
                    backgroundColor: '#FFF',
                    border: '1px solid #D0D5DD',
                    fontSize: '1.4rem',
                    lineHeight: '2rem',
                  }}
                  btnName={'Cancel'}
                  padding={'0.7rem 2.5rem'}
                  clicked={() => setConfirmOpen(false)}
                />
                <Button
                  style={{
                    color: '#FFF',
                    backgroundColor: pendingAction === 'reject' ? '#D92D20' : '#079455',
                    border: `1px solid ${pendingAction === 'reject' ? '#D92D20' : '#079455'}`,
                    fontSize: '1.4rem',
                    lineHeight: '2rem',
                  }}
                  btnName={'Confirm'}
                  padding={'0.7rem 2.5rem'}
                  clicked={() => {
                    setConfirmOpen(false);
                    if (pendingAction === 'reject') {
                      if (onReject) onReject(data, comment.trim());
                    } else {
                      if (onVerify) onVerify(data, comment.trim());
                    }
                  }}
                />
              </Box>
            </Box>
          </Modal>
        )}
        {enableZoom && zoomOpen && (
          <Modal open={zoomOpen} onClose={() => setZoomOpen(false)}>
            {/* Full-screen backdrop: click outside the image to close. */}
            <Box
              sx={{
                position: 'fixed',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                outline: 'none',
                overflow: 'hidden',
              }}
              onClick={() => setZoomOpen(false)}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
            >
              {/* Zoom controls (don't bubble clicks to the backdrop). */}
              <Box
                sx={{
                  position: 'absolute',
                  top: '2rem',
                  right: '2rem',
                  display: 'flex',
                  gap: '1rem',
                  zIndex: 2,
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <Button
                  style={{
                    color: '#101828',
                    backgroundColor: '#FFF',
                    border: '1px solid #D0D5DD',
                    fontSize: '1.8rem',
                    lineHeight: '2rem',
                    minWidth: '4rem',
                  }}
                  btnName={'−'}
                  padding={'0.4rem 1.2rem'}
                  clicked={zoomOut}
                />
                <Button
                  style={{
                    color: '#101828',
                    backgroundColor: '#FFF',
                    border: '1px solid #D0D5DD',
                    fontSize: '1.4rem',
                    lineHeight: '2rem',
                  }}
                  btnName={'Reset'}
                  padding={'0.4rem 1.2rem'}
                  clicked={resetZoom}
                />
                <Button
                  style={{
                    color: '#101828',
                    backgroundColor: '#FFF',
                    border: '1px solid #D0D5DD',
                    fontSize: '1.8rem',
                    lineHeight: '2rem',
                    minWidth: '4rem',
                  }}
                  btnName={'+'}
                  padding={'0.4rem 1.2rem'}
                  clicked={zoomIn}
                />
              </Box>

              <img
                onClick={(e) => e.stopPropagation()}
                onWheel={handleWheel}
                onMouseDown={handleMouseDown}
                draggable={false}
                style={{
                  maxWidth: '90vw',
                  maxHeight: '90vh',
                  objectFit: 'contain',
                  transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
                  transformOrigin: 'center center',
                  transition: dragging ? 'none' : 'transform 0.1s ease-out',
                  cursor:
                    scale > 1 ? (dragging ? 'grabbing' : 'grab') : 'zoom-in',
                  userSelect: 'none',
                }}
                src={process.env.REACT_APP_SUPABASE_BUCKET_PREFIX + data.sitevisiturl}
                alt={''}
              />
            </Box>
          </Modal>
        )}
    </>
  )
}

export default ViewSiteVisitDetails